import { useEffect, useState } from "react";
import axios from "axios";

import publicConfig from "config/public";
import { NotificationSettings } from "custom-types/User";

import useDomainCountryCode from "./useDomainCountryCode";

type Subscription = {
  id: string;
};

export function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * The cached public key fetched using getPublicKey.
 */
let publicKey: string;

export const usePushSubscription = () => {
  /**
   * To recieve push notifications, this browser will need to support both
   * service workers and push notifications (though these likely will always
   * both be supported in tandem).
   */
  const supportsPush = Boolean(
    typeof window !== "undefined" &&
      window.navigator.serviceWorker &&
      window.PushManager &&
      window.Notification,
  );
  const countryCode = useDomainCountryCode();
  const userNotificationServiceUrl =
    countryCode === "CA"
      ? publicConfig.services.userNotificationCaApi.url
      : publicConfig.services.userNotificationApi.url;
  const [subscription, setSubscription] = useState<Subscription>();
  const [settings, setSettings] = useState<NotificationSettings>();
  const [permission, setPermission] = useState(
    supportsPush ? window.Notification.permission : "default",
  );

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSettings().then(setSettings);
    setLoading(false);
    /**
     * This hook only needs to be run once when the code initially loads, even
     * if other state changes.
     */
  }, []);

  const getSubscriptionSettings = async (subscription: { id: string }) =>
    (
      await axios({
        method: "GET",
        url: `${userNotificationServiceUrl}/v2/user/browser/subscriptions/${subscription.id}/settings`,
        withCredentials: true,
      })
    ).data;

  const getDefaultSettings = async () =>
    (
      await axios({
        method: "GET",
        url: `${userNotificationServiceUrl}/v2/settings`,
        withCredentials: true,
      })
    ).data;

  /**
   * Fetches the public key from the server, which is required to create
   * a PushSubscription in the browser.
   *
   * This public key only needs to be fetched once, as it never changes, so we
   * can cache it in memory after the first successful request and re-use the
   * same value to save making additional requests to this endpoint.
   */
  const getPublicKey = async () => {
    if (!publicKey) {
      const keyResponse = await axios({
        method: "GET",
        url: `${userNotificationServiceUrl}/v2/browser/key`,
        withCredentials: true,
      });

      publicKey = keyResponse.data.publicKey;
    }

    return publicKey;
  };

  /**
   * Creates a subscription for the service worker used to receive push events.
   * This operation is idempotent and will return the same subscription in
   * subsequent calls, so this function can be called whenever the subscription
   * is required.
   * @returns PushSubscription (https://developer.mozilla.org/en-US/docs/Web/API/PushSubscription)
   */
  const createPushManagerSubscription = async () => {
    const registration = await window.navigator.serviceWorker.register(
      "/notification-service-worker.js",
    );

    const publicKey = await getPublicKey();

    return registration.pushManager.subscribe({
      applicationServerKey: urlBase64ToUint8Array(publicKey),
      userVisibleOnly: true,
    });
  };

  /**
   * Gets the current PushSubscription, if any. If the user denies permission,
   * returns null and updates React state.
   * @returns PushSubscription (https://developer.mozilla.org/en-US/docs/Web/API/PushSubscription)
   */
  const getPushManagerSubscription = async () => {
    try {
      return await createPushManagerSubscription();
    } catch (error) {
      /**
       * Denying permission will raise a DOMException, so we'll exclusively
       * check for that error and re-raise for any others.
       */
      if (error instanceof DOMException) {
        setPermission(window.Notification.permission);
        return null;
      } else {
        throw error;
      }
    }
  };

  /**
   * Fetches the active push notification subscription from
   * user-notification-service and caches it in React state. Recalling the
   * subscription from this service requires either the ID or the public
   * key of the browser's PushSubscription. The former would require storing
   * the ID in persistant state (such as LocalStorage) which we currently do
   * not do, and the latter requires the user to have granted permission to
   * send push notifications in order to access. For the moment, if the user
   * has not granted permission, this method will return nothing.
   */
  const getBrowserSubscription = async () => {
    if (subscription) {
      return subscription;
    } else {
      const pushSubscription = await getPushManagerSubscription();

      if (!pushSubscription) {
        return null;
      }

      const subscriptionResponse = await axios({
        method: "GET",
        url: `${userNotificationServiceUrl}/v2/user/browser/subscriptions/by-key/${
          pushSubscription?.toJSON()?.keys?.p256dh
        }`,
        withCredentials: true,
      }).catch(() => null);

      if (subscriptionResponse) {
        setSubscription(subscriptionResponse.data);
        return subscriptionResponse.data;
      }
    }
  };

  /**
   * Fetches the settings for the active push notification subscription,
   * otherwise falls back to a generic set of default settings that can be
   * used to populate a UI.
   */
  const getSettings = async () => {
    if (!supportsPush || permission === "denied") {
      /**
       * If push notifications are not available there's no need to load any
       * settings at all.
       *
       * If the user has denied push permission, we can also skip getting the
       * settings, as there isn't a way of getting a browser subscription key
       * to verify that there are not currently subscription settings already
       * configured. Eventually, we may want to add a way of recording the
       * browser subscription ID to make retrieval not dependent on asking for
       * push notification permissions before displaying this UI.
       */
      return null;
    } else if (permission === "default") {
      /**
       * Here we're also in a state where it's not possible to try to fetch
       * previously recorded settings (in the case that a user has turned off
       * and then on push notifications in browser settings). In this case,
       * we can only load the default settings and let a user re-enable the
       * settings that they want.
       */
      return getDefaultSettings();
    } else {
      /**
       * If we do have permission to send push notifications, then we can check
       * that a browser subscription already exists, and retrieve the settings
       * attached to it. If a subscription doesn't already exist (for instance
       * if the marketing push notifications prompt already has asked for and
       * been granted permission), then we'll fall back to loading the default
       * settings.
       */
      const subscription = await getBrowserSubscription();
      if (subscription) {
        return getSubscriptionSettings(subscription);
      } else {
        return getDefaultSettings();
      }
    }
  };

  /**
   * Records that the user has interest in a specific push subject. If a push
   * subscription is not already recorded in user-notification-service, calling
   * this function will instead create a new subscription with an interest in
   * the supplied subject.
   *
   * Note: this subject must already be registered in the configuration files
   * in user-notification-service.
   */
  const followTopic = async (subject: string) => {
    if (!subscription) {
      const pushSubscription = await getPushManagerSubscription();

      if (!pushSubscription) {
        return null;
      }

      const { data: subscription } = await axios({
        data: {
          ...pushSubscription.toJSON(),
          interests: [subject],
        },
        method: "POST",
        url: `${userNotificationServiceUrl}/v2/user/browser/subscriptions`,
        withCredentials: true,
      });

      setSubscription(subscription);
      setSettings(await getSubscriptionSettings(subscription));
    } else {
      await axios({
        data: { subject },
        method: "POST",
        url: `${userNotificationServiceUrl}/v2/user/browser/subscriptions/${subscription.id}/interests`,
        withCredentials: true,
      });

      setSettings(await getSubscriptionSettings(subscription));
    }
  };

  /**
   * Records that the user has interest in a specific push topic.
   */
  const unfollowTopic = async (subject: string) => {
    if (!subscription) {
      // Nothing to do if there's no subscription for which to unfollow
      return null;
    }

    await axios({
      method: "DELETE",
      url: `${userNotificationServiceUrl}/v2/user/browser/subscriptions/${subscription.id}/interests/${subject}`,
      withCredentials: true,
    });

    setSettings(await getSubscriptionSettings(subscription));
  };

  return {
    followTopic,
    loading,
    permission,
    settings,
    supportsPush,
    unfollowTopic,
  };
};
